import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { addOrder } from '../../store/slices/ordersSlice';
import { addClient } from '../../store/slices/clientsSlice';
import { addReviewItem } from '../../store/slices/reviewQueueSlice';
import { addToast } from '../../store/slices/toastsSlice';
import { ParsedEmailOrder } from '../../utils/emailParser';
import {
  processEmailOrder,
  findOrPrepareClient,
  createClientFromParsedData,
  createOrderFromParsedData
} from '../../utils/emailOrderProcessor';

interface ProcessEmailOrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  parsedEmails: ParsedEmailOrder[];
  availableRecipes: { id: string; name: string }[];
}

interface ProcessingStatus {
  total: number;
  processed: number;
  autoCreated: number;
  sentToReview: number;
  errors: string[];
}

const ProcessEmailOrdersModal: React.FC<ProcessEmailOrdersModalProps> = ({
  isOpen,
  onClose,
  parsedEmails,
  availableRecipes
}) => {
  const dispatch = useAppDispatch();
  const orders = useAppSelector(state => state.orders.orders);
  const clients = useAppSelector(state => state.clients.clients);
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState<ProcessingStatus>({
    total: 0,
    processed: 0,
    autoCreated: 0,
    sentToReview: 0,
    errors: []
  });

  if (!isOpen) return null;

  const handleProcess = () => {
    setIsProcessing(true);
    const newStatus: ProcessingStatus = {
      total: parsedEmails.length,
      processed: 0,
      autoCreated: 0,
      sentToReview: 0,
      errors: []
    };

    console.log(`[ProcessEmailOrders] Starting processing of ${parsedEmails.length} emails`);

    parsedEmails.forEach((parsedEmail, index) => {
      try {
        // Process each email through validation and duplicate detection
        const result = processEmailOrder(parsedEmail, orders);

        if (result.action === 'auto-create') {
          // Auto-create order and client
          const clientResult = findOrPrepareClient(result.parsedData!.email, result.parsedData!.phone, clients);

          // Create client if it doesn't exist
          if (!clientResult.isExisting) {
            const newClient = createClientFromParsedData(result.parsedData!, clientResult.clientId);
            dispatch(addClient(newClient));
            console.log(`[ProcessEmailOrders] ✓ Created new client: ${newClient.name}`);
          }

          // Create order
          const newOrder = createOrderFromParsedData(result.parsedData!, clientResult.clientId, availableRecipes);
          dispatch(addOrder(newOrder));

          newStatus.autoCreated++;
          console.log(`[ProcessEmailOrders] ✓ Auto-created order for ${result.parsedData!.customerName}`);
        } else if (result.action === 'review-queue') {
          // Add to review queue
          dispatch(addReviewItem(result.reviewQueueItem!));
          newStatus.sentToReview++;
          console.log(
            `[ProcessEmailOrders] → Sent to review queue: ${result.reviewQueueItem!.parsedData.customerName} (${result.flagReason})`
          );
        }

        newStatus.processed++;
      } catch (error) {
        const errorMsg = `Email ${index + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`;
        newStatus.errors.push(errorMsg);
        console.error(`[ProcessEmailOrders] ✗ Error processing email ${index + 1}:`, error);
      }
    });

    setStatus(newStatus);
    setIsProcessing(false);

    // Show summary toast
    if (newStatus.errors.length === 0) {
      dispatch(
        addToast(
          `Processing complete: ${newStatus.autoCreated} auto-created, ${newStatus.sentToReview} sent to review`,
          'success'
        )
      );
    } else {
      dispatch(addToast(`Processing complete with ${newStatus.errors.length} errors`, 'error'));
    }

    console.log('[ProcessEmailOrders] ✓ Processing complete:', newStatus);
  };

  const handleClose = () => {
    setStatus({
      total: 0,
      processed: 0,
      autoCreated: 0,
      sentToReview: 0,
      errors: []
    });
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isProcessing) {
      handleClose();
    }
  };

  const hasProcessed = status.processed > 0;

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          width: '100%',
          maxWidth: '600px',
          padding: '32px'
        }}
      >
        <h2 style={{ margin: '0 0 24px 0', fontSize: '1.5rem', color: '#333' }}>Process Email Orders</h2>

        {!hasProcessed ? (
          <>
            <div style={{ marginBottom: '24px', fontSize: '0.95rem', color: '#666' }}>
              <p style={{ margin: '0 0 12px 0' }}>
                Ready to process <strong>{parsedEmails.length}</strong> parsed{' '}
                {parsedEmails.length === 1 ? 'email' : 'emails'}.
              </p>
              <p style={{ margin: '0 0 12px 0' }}>The system will:</p>
              <ul style={{ margin: '0 0 12px 0', paddingLeft: '24px' }}>
                <li>Validate each email for required fields (name, email, phone, address, items)</li>
                <li>Check for duplicate orders from the same email within the last 7 days</li>
                <li>
                  <strong>Auto-create</strong> orders and clients for valid emails with no duplicates
                </li>
                <li>
                  <strong>Send to review queue</strong> any emails that are incomplete or duplicate
                </li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleClose}
                disabled={isProcessing}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  opacity: isProcessing ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleProcess}
                disabled={isProcessing}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  opacity: isProcessing ? 0.5 : 1
                }}
              >
                {isProcessing ? 'Processing...' : 'Start Processing'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                marginBottom: '24px',
                padding: '20px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
              }}
            >
              <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#333' }}>Processing Results</h3>

              <div style={{ display: 'grid', gap: '12px', fontSize: '0.95rem' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px',
                    backgroundColor: 'white',
                    borderRadius: '4px'
                  }}
                >
                  <span>Total Emails:</span>
                  <strong>{status.total}</strong>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px',
                    backgroundColor: '#E8F5E9',
                    borderRadius: '4px',
                    color: '#2E7D32'
                  }}
                >
                  <span>Auto-Created Orders:</span>
                  <strong>{status.autoCreated}</strong>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px',
                    backgroundColor: '#FFF3E0',
                    borderRadius: '4px',
                    color: '#E65100'
                  }}
                >
                  <span>Sent to Review Queue:</span>
                  <strong>{status.sentToReview}</strong>
                </div>
                {status.errors.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px',
                      backgroundColor: '#FFEBEE',
                      borderRadius: '4px',
                      color: '#C62828'
                    }}
                  >
                    <span>Errors:</span>
                    <strong>{status.errors.length}</strong>
                  </div>
                )}
              </div>
            </div>

            {status.errors.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#C62828' }}>Errors Encountered:</h4>
                <div
                  style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    padding: '12px',
                    backgroundColor: '#FFEBEE',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontFamily: 'monospace'
                  }}
                >
                  {status.errors.map((error, index) => (
                    <div key={index} style={{ marginBottom: '4px', color: '#C62828' }}>
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleClose}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '600'
                }}
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProcessEmailOrdersModal;
